import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { get, isEmpty } from 'lodash';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import { preloadImage } from '@/client/utils/image';
import Liff, { getLineId } from '@/client/utils/liff';
import { AnswerDialog } from './dialog';
import styles from './styles';

const SERVER_URL = __SERVER_URL__;

const useStyles = makeStyles(createStyles(styles));

const Quiz = ({ config }) => {
  const { id, questionSvgs, quizBackground, totalQuestion } = config;

  const classes = useStyles({ quizBackground });
  const history = useHistory();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [questionNum, setQuestionNum] = useState(0);

  const { getValues, control, handleSubmit, errors, setValue } = useForm({
    mode: 'onBlur',
    shouldUnregister: false,
  });

  const loadQuestions = async () => {
    try {
      const lineId = await getLineId();
      const resp = await fetch(`${SERVER_URL}/disease-quiz/questions`, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        method: 'POST',
        body: JSON.stringify({ lineId, diseaseId: id }),
      });
      if (!resp.ok && resp.status === 401) {
        alert('請確認有加疾管家為好友，閱讀並同意服務聲明後再試一次！');
        Liff.closeWindow();
        return;
      }
      const { questions: respQuestions } = await resp.json();
      setQuestions(respQuestions);
      setLoading(false);
    } catch (error) {
      throw new Error(`failed to get questions: ${error}`);
    }
  };

  const handleNext = () => {
    if (questionNum < totalQuestion - 1) {
      setDialogOpen(false);
      setQuestionNum(q => q + 1);
      return;
    }
    const answers = questions.map(q => q.answer);
    const userAnswers = getValues('userAnswers');
    const correctNum = answers.reduce((score, answer, i) => score + Number(answer === userAnswers[i]), 0);

    history.replace(`/disease-quiz-${id}/result?liffId=${Liff.id}`, { questions, correctNum });
  };

  const currentQuestion = useMemo(() => questions[questionNum], [questions, questionNum]);

  useEffect(() => {
    setValue(`userAnswers[${questionNum}]`, '');
  }, [questionNum]);

  useEffect(() => {
    loadQuestions();
    preloadImage(questionSvgs);
  }, []);

  if (loading) {
    return (
      <Box className={classes.container} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Fragment>
      <Box className={classes.container}>
        <Box display="flex" alignItems="center">
          <Box className={classes.questionNumIcon}>
            <img src={questionSvgs[questionNum]} alt="question number" />
          </Box>
          <Typography variant="body1" className={classes.questionContent}>
            {currentQuestion.content}
          </Typography>
        </Box>
        <Box style={{ marginTop: 36 }}>
          <FormControl
            className={classes.formControl}
            variant="outlined"
            error={!isEmpty(`errors.userAnswers[${questionNum}]`)}
            style={{ marginBottom: 8 }}
          >
            <Controller
              name={`userAnswers[${questionNum}]`}
              defaultValue=""
              control={control}
              rules={{ required: '請作答' }}
              render={({ onChange, value: radioValue, ref }) => (
                <RadioGroup
                  value={radioValue}
                  onChange={e => onChange(Number(e.target.value))}
                  aria-labelledby="select-option-label"
                >
                  {currentQuestion.options.map((option, i) => (
                    <FormControlLabel
                      key={option}
                      label={option}
                      value={i}
                      inputRef={ref}
                      control={<Radio required color="primary" />}
                    />
                  ))}
                </RadioGroup>
              )}
            />
            <FormHelperText>{get(errors, ['userAnswers', questionNum, 'message'], '') || ' '}</FormHelperText>
          </FormControl>
        </Box>
        <Box display="flex" justifyContent="center" style={{ width: '100%', marginTop: 160 }}>
          <Button
            className={classes.nextButton}
            variant="contained"
            color="primary"
            onClick={handleSubmit(() => {
              setDialogOpen(true);
            })}
          >
            送出
          </Button>
        </Box>
      </Box>
      {dialogOpen && (
        <AnswerDialog
          open
          isCorrect={getValues('userAnswers')[questionNum] === currentQuestion.answer}
          isFinal={questionNum === totalQuestion - 1}
          question={currentQuestion}
          config={config}
          onClick={handleSubmit(handleNext)}
        />
      )}
    </Fragment>
  );
};

export default Quiz;
